import React, { createContext, useCallback, useContext } from 'react';
import _get from 'lodash/get';
import _has from 'lodash/has';

import { PageErrorUi } from 'components';
import Loader from './Loader';
import Form from './Form';
import { useFetch, useForm, useMutation, useSegment } from 'utils/hooks';
import { createFormData } from './utils';
import { AuthContext } from 'context/AuthContext';
import { useStoreOnboarding } from '../../../../context';
import { DEFAULT_FORM_DATA, FORM_SCHEMA } from './stubs';

/**
 * @type {React.Context<{
 *  data: import("zod").z.infer<typeof FORM_SCHEMA>,
 *  errors: Partial<Record<keyof import("zod").z.infer<typeof FORM_SCHEMA>, string>>,
 *  onChange: (updates: Partial<import("zod").z.infer<typeof FORM_SCHEMA>>, config?: import('utils/types').FormValidationConfig) => void,
 *  isDirty: boolean,
 *  isProcessing: boolean,
 *  onSave: (onDone?: (err: Error, data: any) => void) => void
 * }}}
 */
const ExchangesConfigContext = createContext();

export function useExchangesConfig() {
  const context = useContext(ExchangesConfigContext);
  if (!context) {
    throw new Error('useExchanges must be used within an ExchangesProvider');
  }
  return context;
}

export function ExchangesConfigProvider({ children }) {
  const { todos, onCreateTasks, onDeleteTasks } = useStoreOnboarding();
  const { track } = useSegment();

  const { updateState } = useContext(AuthContext);

  const hasExchangesConfigTask = _has(todos, 'EXCHANGES_CONFIG');

  const {
    data,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    defaultData: DEFAULT_FORM_DATA,
    dataSchema: FORM_SCHEMA
  });

  const { isLoading, isError, fetchData } = useFetch(
    'configure.featureFlags.root',
    {
      payload: { filter: 'is_exchange_allowed' },
      onTransform: data => _get(data, '[0]', null),
      onSuccess: data => {
        const formData = createFormData(data, hasExchangesConfigTask);
        onSetInitialData(formData);
      }
    }
  );

  const { mutate, isProcessing } = useMutation(
    'configure.featureFlags.root',
    'PATCH',
    {
      routeParam: data.id,
      payload: () => ({ is_enabled: data.isEnabled }),
      errorMessage: 'Failed to save the exchanges preference.',
      onValidate: () => onValidate(data),
      onSuccess: () => {
        track('FTU Wizard Exchanges Configured', {
          is_enabled: data.isEnabled,
          added_to_do_later: data.isAddedToDoLater
        });
        updateState({ isExchangeAllowed: data.isEnabled });
      }
    }
  );

  const onSave = useCallback(
    callback => {
      mutate({}, callback);
      const todoUpdater =
        !data.isEnabled && data.isAddedToDoLater
          ? !hasExchangesConfigTask
            ? onCreateTasks
            : () => {}
          : hasExchangesConfigTask
          ? onDeleteTasks
          : () => {};
      todoUpdater({ todos: ['EXCHANGES_CONFIG'] });
    },
    [data, hasExchangesConfigTask, mutate, onCreateTasks, onDeleteTasks]
  );

  return (
    <ExchangesConfigContext.Provider
      value={{
        data,
        errors,
        isDirty,
        isProcessing,
        onChange,
        onSave
      }}
    >
      <PageErrorUi isError={isError} onReload={fetchData}>
        <Loader isLoading={isLoading}>
          <Form />
          {children}
        </Loader>
      </PageErrorUi>
    </ExchangesConfigContext.Provider>
  );
}
